import React, { useEffect, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { routes } from '@/lib/routes';
import { getWorkRequests } from '../api';
import type { PassportTab } from './RequestPassportModal';
import { RequestPassportModal } from './RequestPassportModal';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import type { WorkRequestKpis, WorkRequestListItem } from '../types';

interface WorkRequestsRegistryProps {
  roleContext?: string;
  onBack?: () => void;
  onClose?: () => void;
  initialFilter?: string;
  isStandalone?: boolean;
}

const FILTERS: Array<{ id: string; label: string }> = [
  { id: 'ALL', label: 'Все' },
  { id: 'IN_PROGRESS', label: 'В работе' },
  { id: 'NEEDS_CLARIFICATION', label: 'Требуют уточнения' },
  { id: 'OVERDUE', label: 'Просрочено' },
  { id: 'NOT_STARTED', label: 'Не запущены' },
  { id: 'DRAFT', label: 'Созданные / черновики' },
  { id: 'COMPLETED', label: 'Завершённые' },
  { id: 'CANCELLED', label: 'Отменённые' },
];

const STATUS_COLOR: Record<string, string> = {
  IN_PROGRESS: '#49ddb7',
  ACTIVE: '#49ddb7',
  NEEDS_CLARIFICATION: '#e2ad3a',
  NOT_STARTED: '#66b7e8',
  NEW: '#66b7e8',
  COMPLETED: '#41d2ff',
  DONE: '#41d2ff',
  CANCELLED: '#ff7b65',
  DRAFT: '#9e80ff',
};

function roleLabel(code?: string | null): string {
  if (!code) return '';
  const map: Record<string, string> = {
    DOCTORAL: 'Докторант',
    LEARNER: 'Докторант',
    AUTHOR: 'Автор / соавтор',
    CLIENT: 'Клиент',
    TEACHER: 'Преподаватель',
    ADVISOR: 'Эдвайзер',
    SUPERVISOR: 'Научный руководитель',
    SYSTEM_OWNER: 'Владелец системы',
    OPERATIONS_PARTNER: 'Операционный партнёр',
    RESEARCHER: 'Исследователь',
  };
  return map[code] || code;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('ru-RU');
  } catch {
    return iso;
  }
}

export const WorkRequestsRegistry: React.FC<WorkRequestsRegistryProps> = ({
  roleContext,
  initialFilter = 'ALL',
}) => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<WorkRequestListItem[]>([]);
  const [counts, setCounts] = useState<WorkRequestKpis>({
    total: 0,
    in_progress: 0,
    needs_clarification: 0,
    overdue: 0,
    not_started: 0,
    completed: 0,
    draft: 0,
    cancelled: 0,
  });
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('ACTION_REQUIRED');
  const [loading, setLoading] = useState(true);

  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [passportInitialTab, setPassportInitialTab] = useState<PassportTab>('overview');
  const [isPassportOpen, setIsPassportOpen] = useState(false);
  const [communicationRequest, setCommunicationRequest] = useState<WorkRequestListItem | null>(null);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await getWorkRequests({
        filter: activeFilter,
        search: search.trim() || undefined,
        sort: sortBy,
        role_context: roleContext,
      });
      setRequests(res.items || []);
      setCounts(res.counts);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchRequests();
  }, [activeFilter, search, sortBy, roleContext]);

  const handleOpenPassport = (reqId: string, tab: PassportTab = 'overview') => {
    setSelectedRequestId(reqId);
    setPassportInitialTab(tab);
    setIsPassportOpen(true);
  };

  const filterCount = (id: string): number => {
    switch (id) {
      case 'ALL':
        return counts.total;
      case 'IN_PROGRESS':
        return counts.in_progress;
      case 'NEEDS_CLARIFICATION':
        return counts.needs_clarification;
      case 'OVERDUE':
        return counts.overdue;
      case 'NOT_STARTED':
        return counts.not_started;
      case 'DRAFT':
        return counts.draft;
      case 'COMPLETED':
        return counts.completed;
      case 'CANCELLED':
        return counts.cancelled;
      default:
        return 0;
    }
  };

  return (
    <>
      <div className="owner-uat-root">
        <section className="owner-uat-route-hero" style={{ '--owner-uat-accent': '#41d2ff' } as CSSProperties}>
          <div>
            <div className="owner-uat-route-kicker">Реестр заявок</div>
            <h2>Заявки в работе</h2>
            <p>
              Реестр, статусы, уточнения, история и коммуникация. Создание новой заявки вынесено в
              отдельную карточку «Создать заявку».
            </p>
          </div>
          <div className="owner-uat-actions">
            <button
              type="button"
              className="owner-uat-btn primary"
              onClick={() => navigate(routes.newRequest)}
            >
              + Создать заявку
            </button>
          </div>
        </section>

        <section className="owner-uat-kpis">
          <article
            className="owner-uat-kpi"
            style={{ '--kpi': '#49ddb7' } as CSSProperties}
            onClick={() => setActiveFilter('IN_PROGRESS')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setActiveFilter('IN_PROGRESS')}
          >
            <small>В работе</small>
            <strong>{counts.in_progress}</strong>
            <span>активно исполняются</span>
          </article>
          <article
            className="owner-uat-kpi"
            style={{ '--kpi': '#e2ad3a' } as CSSProperties}
            onClick={() => setActiveFilter('NEEDS_CLARIFICATION')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setActiveFilter('NEEDS_CLARIFICATION')}
          >
            <small>Требуют уточнения</small>
            <strong>{counts.needs_clarification}</strong>
            <span>ожидается обязательный ответ</span>
          </article>
          <article
            className="owner-uat-kpi"
            style={{ '--kpi': '#ff7b65' } as CSSProperties}
            onClick={() => setActiveFilter('OVERDUE')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setActiveFilter('OVERDUE')}
          >
            <small>Просрочено</small>
            <strong>{counts.overdue}</strong>
            <span>срок исполнения истёк</span>
          </article>
          <article
            className="owner-uat-kpi"
            style={{ '--kpi': '#41d2ff' } as CSSProperties}
            onClick={() => setActiveFilter('ALL')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setActiveFilter('ALL')}
          >
            <small>Всего</small>
            <strong>{counts.total}</strong>
            <span>заявок в текущем scope</span>
          </article>
        </section>

        <section className="owner-uat-panel">
          <header>
            <div>
              <h3>Фильтры заявок</h3>
              <p>Новые сверху · поиск по коду заявки, имени, ID, теме и услуге.</p>
            </div>
          </header>
          <div className="owner-uat-panel-body">
            <div className="owner-uat-toolbar">
              <div className="owner-uat-switch">
                {FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    className={`owner-uat-switch-btn${activeFilter === filter.id ? ' active' : ''}`}
                    onClick={() => setActiveFilter(filter.id)}
                  >
                    {filter.label}
                    {filter.id !== 'ALL' ? ` (${filterCount(filter.id)})` : ''}
                  </button>
                ))}
              </div>
              <input
                className="owner-uat-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="код заявки · имя · ID · тема · услуга"
              />
              <select
                className="owner-uat-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="ACTION_REQUIRED">Сначала требующие действия</option>
                <option value="DUE_DATE">По сроку исполнения</option>
                <option value="REFERENCE_CODE">По коду заявки</option>
              </select>
            </div>
          </div>
        </section>

        <section className="owner-uat-panel">
          <header>
            <div>
              <h3>Найдено: {loading ? '…' : requests.length}</h3>
              <p>В строке только оперативно важные сведения; полный набор ответов открывается отдельно.</p>
            </div>
          </header>
          <div className="owner-uat-panel-body" style={{ padding: 0 }}>
            {loading ? (
              <div className="owner-uat-empty" style={{ margin: 14 }}>
                Загрузка реестра заявок…
              </div>
            ) : null}
            {!loading && !requests.length ? (
              <div className="owner-uat-empty" style={{ margin: 14 }}>
                По выбранному фильтру заявок нет.
              </div>
            ) : null}
            {!loading && requests.length > 0 ? (
              <div className="owner-uat-request-list">
                {requests.map((r) => {
                  const role = roleLabel(r.requester_code || r.requester_role);
                  const needsClarify =
                    r.status === 'NEEDS_CLARIFICATION' || r.pending_clarification;
                  return (
                    <article key={r.id} className="owner-uat-request-card">
                      <div>
                        <strong>
                          {r.reference_code} · {r.requester_name}
                        </strong>
                        <small>
                          {formatDate(r.created_at)}
                          {role ? ` · ${role}` : ''}
                          {r.requester_id ? ` · ${r.requester_id}` : ''}
                        </small>
                        <small>
                          {r.direction} → {r.service_name}
                        </small>
                      </div>
                      <div>
                        <strong>{r.title || r.service_name}</strong>
                        <small>
                          {r.stage || 'Этап не указан'}
                          {r.assignee ? ` · ${r.assignee}` : ' · Исполнитель не назначен'}
                        </small>
                      </div>
                      <div>
                        <span
                          className="owner-uat-chip"
                          style={
                            {
                              '--badge': STATUS_COLOR[r.status] || '#41d2ff',
                            } as CSSProperties
                          }
                        >
                          {r.status_label}
                        </span>
                        <small>{r.blocking_reason || 'Нет блокировки'}</small>
                      </div>
                      <div>
                        <strong>Срок: {r.due_at_label || formatDate(r.due_at)}</strong>
                        <small>
                          {needsClarify
                            ? 'Нужен ответ до продолжения'
                            : 'Следующее действие доступно в карточке'}
                        </small>
                      </div>
                      <div className="owner-uat-req-actions">
                        <button
                          type="button"
                          className="owner-uat-btn"
                          onClick={() => handleOpenPassport(r.id, 'overview')}
                        >
                          Открыть
                        </button>
                        {needsClarify ? (
                          <button
                            type="button"
                            className="owner-uat-btn"
                            onClick={() => handleOpenPassport(r.id, 'clarifications')}
                          >
                            Уточнение
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="owner-uat-btn"
                          onClick={() => setCommunicationRequest(r)}
                        >
                          Связь
                        </button>
                        <button
                          type="button"
                          className="owner-uat-btn"
                          onClick={() => handleOpenPassport(r.id, 'answers')}
                        >
                          Все ответы
                        </button>
                        <button
                          type="button"
                          className="owner-uat-btn"
                          onClick={() => handleOpenPassport(r.id, 'timeline')}
                        >
                          История
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : null}
          </div>
        </section>
      </div>

      <RequestPassportModal
        isOpen={isPassportOpen}
        requestIdOrCode={selectedRequestId}
        initialTab={passportInitialTab}
        onClose={() => setIsPassportOpen(false)}
        roleContext={roleContext}
      />
      <RoleAcademicConnectionsOverlay
        isOpen={Boolean(communicationRequest)}
        onClose={() => setCommunicationRequest(null)}
        roleContext={roleContext || 'SYSTEM_OWNER'}
        initialTarget={
          communicationRequest
            ? {
                linkedObject: {
                  type: 'REQUEST',
                  id: communicationRequest.reference_code || communicationRequest.id,
                  title: communicationRequest.title || communicationRequest.service_name,
                },
              }
            : undefined
        }
      />
    </>
  );
};
